import { create } from 'zustand';
import type {
  SegmentationCondition,
  SegmentationConnector,
  SegmentationCriteria,
  SegmentationGroup,
  SegmentationLayout,
} from '@/shared/types/segmentation';
import { createEmptyCondition, createEmptyGroup, generateLocalId } from './segmentation-mapper';

/**
 * Estado do canvas — rascunho local (client-side), nunca persiste sozinho.
 * `applyCriteria` (chamado pela IA, tool `updateSegmentationDraft`) e as ações
 * manuais de grupo/condição mexem só aqui; salvar de verdade é uma chamada
 * explícita à API (ver ui/segmentation-builder-page.tsx e
 * ui/segmentation-chat-widget.tsx, tool `segmentationReady`).
 *
 * Primeiro uso real de zustand neste projeto (já era dependência, nunca usada).
 */
interface SegmentationCanvasState {
  groups: SegmentationGroup[];
  connectors: SegmentationConnector[];
  positions: NonNullable<SegmentationLayout['positions']>;
  dirty: boolean;

  loadCriteria: (criteria: SegmentationCriteria, layout?: SegmentationLayout) => void;
  applyCriteria: (criteria: SegmentationCriteria) => void;
  reset: () => void;

  addGroup: () => void;
  removeGroup: (groupId: string) => void;
  duplicateGroup: (groupId: string) => void;
  setGroupConnector: (groupId: string, condition: SegmentationConnector) => void;
  setChainConnector: (index: number, connector: SegmentationConnector) => void;

  addCondition: (groupId: string) => void;
  updateCondition: (groupId: string, conditionId: string, patch: Partial<SegmentationCondition>) => void;
  removeCondition: (groupId: string, conditionId: string) => void;

  setNodePosition: (groupId: string, position: { x: number; y: number }) => void;
}

const emptyState = {
  groups: [] as SegmentationGroup[],
  connectors: [] as SegmentationConnector[],
  positions: {} as NonNullable<SegmentationLayout['positions']>,
};

export const useSegmentationCanvasStore = create<SegmentationCanvasState>((set) => ({
  ...emptyState,
  dirty: false,

  loadCriteria: (criteria, layout) =>
    set({
      groups: criteria.groups,
      connectors: criteria.connectors,
      positions: layout?.positions ?? {},
      dirty: false,
    }),

  applyCriteria: (criteria) =>
    set({ groups: criteria.groups, connectors: criteria.connectors, dirty: true }),

  reset: () => set({ ...emptyState, dirty: false }),

  addGroup: () =>
    set((state) => ({
      groups: [...state.groups, createEmptyGroup()],
      connectors: state.groups.length > 0 ? [...state.connectors, 'AND'] : state.connectors,
      dirty: true,
    })),

  removeGroup: (groupId) =>
    set((state) => {
      const index = state.groups.findIndex((g) => g.id === groupId);
      if (index === -1) return state;
      const groups = state.groups.filter((g) => g.id !== groupId);
      // Remove o conector que ligava esse grupo ao anterior (ou ao próximo, se for o primeiro).
      const connectorIndex = index === 0 ? 0 : index - 1;
      const connectors = state.connectors.filter((_, i) => i !== connectorIndex);
      return { groups, connectors, dirty: true };
    }),

  duplicateGroup: (groupId) =>
    set((state) => {
      const original = state.groups.find((g) => g.id === groupId);
      if (!original) return state;
      const copy: SegmentationGroup = {
        id: generateLocalId('grp'),
        condition: original.condition,
        conditions: original.conditions.map((c) => ({ ...c, id: generateLocalId('cond') })),
      };
      const index = state.groups.findIndex((g) => g.id === groupId);
      const groups = [...state.groups.slice(0, index + 1), copy, ...state.groups.slice(index + 1)];
      const connectors = [...state.connectors.slice(0, index), 'AND' as const, ...state.connectors.slice(index)];
      return { groups, connectors, dirty: true };
    }),

  setGroupConnector: (groupId, condition) =>
    set((state) => ({
      groups: state.groups.map((g) => (g.id === groupId ? { ...g, condition } : g)),
      dirty: true,
    })),

  setChainConnector: (index, connector) =>
    set((state) => {
      const connectors = [...state.connectors];
      connectors[index] = connector;
      return { connectors, dirty: true };
    }),

  addCondition: (groupId) =>
    set((state) => ({
      groups: state.groups.map((g) =>
        g.id === groupId ? { ...g, conditions: [...g.conditions, createEmptyCondition()] } : g,
      ),
      dirty: true,
    })),

  updateCondition: (groupId, conditionId, patch) =>
    set((state) => ({
      groups: state.groups.map((g) =>
        g.id !== groupId
          ? g
          : {
              ...g,
              conditions: g.conditions.map((c) => (c.id === conditionId ? { ...c, ...patch } : c)),
            },
      ),
      dirty: true,
    })),

  removeCondition: (groupId, conditionId) =>
    set((state) => ({
      groups: state.groups.map((g) =>
        g.id !== groupId ? g : { ...g, conditions: g.conditions.filter((c) => c.id !== conditionId) },
      ),
      dirty: true,
    })),

  setNodePosition: (groupId, position) =>
    set((state) => ({ positions: { ...state.positions, [groupId]: position } })),
}));
